import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Button,
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  ErrorState,
  Field,
  Input,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Skeleton,
  Switch,
  Textarea,
  useToast,
} from '@/components/ui';
import { ApiError } from '@/lib/api';
import { dirOf, LOCALES, setLocale, type Locale } from '@/lib/i18n';
import { scheduleIssue } from '@/features/quotes/payment-terms';
import { PaymentTermsEditor, useScheduleIssueText } from '@/features/quotes/payment-terms-editor';
import { TRADE_KEYS, type TradeKey } from '@/lib/schemas';
import { TRADES } from '@/lib/trade';
import { useFeatures } from '@/lib/features';
import {
  BUSINESS_SETTINGS_KEY,
  businessApi,
  type BusinessSettings,
} from './business-api';

/**
 * פרטי העסק (מופיעים בראש כל הצעת מחיר) וברירות המחדל להצעה: תוקף, מע"מ,
 * לוח תשלומים וסעיפים. כל הצעה מקבלת עותק ברגע היצירה.
 */
export function BusinessPanel() {
  const { t } = useTranslation(['settings', 'common']);
  const scheduleIssueText = useScheduleIssueText();
  const toast = useToast();
  const features = useFeatures();
  const qc = useQueryClient();
  const settings = useQuery({
    queryKey: BUSINESS_SETTINGS_KEY,
    queryFn: ({ signal }) => businessApi.get(signal),
  });
  const [draft, setDraft] = useState<BusinessSettings | null>(null);
  useEffect(() => {
    if (settings.data && !draft) setDraft(settings.data);
  }, [settings.data, draft]);

  const save = useMutation({
    mutationFn: (body: BusinessSettings) => businessApi.update(body),
    onSuccess: (saved) => {
      const tradeChanged = saved.trade !== settings.data?.trade;
      const localeChanged = saved.locale !== settings.data?.locale;
      qc.setQueryData(BUSINESS_SETTINGS_KEY, saved);
      setDraft(saved);
      toast.success(t('business.saved'), t('business.savedHint'));
      // שפה חדשה — setLocale טוען מחדש את הדף (וכך גם שינוי תחום נקלט).
      if (localeChanged && setLocale(saved.locale)) return;
      // התחום משנה מונחים ותפריטים בכל הממשק — טוענים מחדש את הסשן.
      if (tradeChanged) window.location.reload();
    },
    onError: (e) => toast.error(t('common:errors.saveFailed'), e instanceof ApiError ? e.message : undefined),
  });

  if (settings.isPending || !draft) return <Skeleton className="h-96" />;
  if (settings.isError)
    return <ErrorState error={settings.error} onRetry={() => void settings.refetch()} />;

  const p = draft.profile;
  const q = draft.quoteDefaults;
  const setProfile = (patch: Partial<typeof p>) =>
    setDraft({ ...draft, profile: { ...p, ...patch } });
  const setQuote = (patch: Partial<typeof q>) =>
    setDraft({ ...draft, quoteDefaults: { ...q, ...patch } });
  const termsIssue = scheduleIssue(q.paymentTerms.schedule);
  const valid = termsIssue === null;

  return (
    <div className="space-y-5">
      <Card>
        <CardHeader>
          <CardTitle>{t('business.trade.title')}</CardTitle>
          <CardDescription>{t('business.trade.description')}</CardDescription>
        </CardHeader>
        <CardContent>
          <Select
            value={draft.trade}
            onValueChange={(v) => setDraft({ ...draft, trade: v as TradeKey })}
          >
            <SelectTrigger aria-label={t('business.trade.title')} className="sm:w-72">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {TRADE_KEYS.map((k) => (
                <SelectItem key={k} value={k}>
                  {TRADES[k].label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>{t('common:language.label')}</CardTitle>
          <CardDescription>{t('business.language.hint')}</CardDescription>
        </CardHeader>
        <CardContent>
          <Select value={draft.locale} onValueChange={(v) => setDraft({ ...draft, locale: v as Locale })}>
            <SelectTrigger aria-label={t('common:language.label')} className="sm:w-72">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {LOCALES.map((l) => (
                <SelectItem key={l} value={l}>
                  {t(`common:language.${l}`)}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>{t('business.profile.title')}</CardTitle>
          <CardDescription>{t('business.profile.description')}</CardDescription>
        </CardHeader>
        <CardContent className="grid gap-4 sm:grid-cols-2">
          <Field label={t('business.profile.legalName')} htmlFor="biz-name" hint={t('business.profile.legalNameHint')}>
            <Input
              id="biz-name"
              value={p.legalName}
              maxLength={200}
              onChange={(e) => setProfile({ legalName: e.target.value })}
            />
          </Field>
          <Field label={t('business.profile.taxId')} htmlFor="biz-tax">
            <Input
              id="biz-tax"
              dir="ltr"
              inputMode="numeric"
              value={p.taxId}
              onChange={(e) => setProfile({ taxId: e.target.value })}
            />
          </Field>
          <Field label={t('business.profile.phone')} htmlFor="biz-phone">
            <Input
              id="biz-phone"
              type="tel"
              dir="ltr"
              value={p.phone}
              onChange={(e) => setProfile({ phone: e.target.value })}
            />
          </Field>
          <Field label={t('business.profile.email')} htmlFor="biz-email">
            <Input
              id="biz-email"
              type="email"
              dir="ltr"
              value={p.email}
              onChange={(e) => setProfile({ email: e.target.value })}
            />
          </Field>
          <div className="sm:col-span-2">
            <Field label={t('business.profile.address')} htmlFor="biz-address">
              <Input
                id="biz-address"
                value={p.address}
                maxLength={300}
                onChange={(e) => setProfile({ address: e.target.value })}
              />
            </Field>
          </div>
        </CardContent>
      </Card>

      {features.has('quotes') ? (
        <>
          <Card>
            <CardHeader>
              <CardTitle>{t('business.quotes.title')}</CardTitle>
              <CardDescription>{t('business.quotes.description')}</CardDescription>
            </CardHeader>
            <CardContent className="space-y-5">
              <div className="grid gap-4 sm:grid-cols-3">
                <Field label={t('business.quotes.validDays')} htmlFor="q-valid">
                  <Input
                    id="q-valid"
                    type="number"
                    dir="ltr"
                    min={1}
                    max={90}
                    value={q.validDays}
                    onChange={(e) =>
                      setQuote({ validDays: Math.round(Number(e.target.value)) || 1 })
                    }
                  />
                </Field>
                <Field label={t('business.quotes.vat')} htmlFor="q-vat">
                  <Select
                    value={q.vatMode}
                    onValueChange={(v) => setQuote({ vatMode: v as typeof q.vatMode })}
                  >
                    <SelectTrigger id="q-vat">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="added">{t('business.quotes.vatAdded')}</SelectItem>
                      <SelectItem value="included">{t('business.quotes.vatIncluded')}</SelectItem>
                      <SelectItem value="exempt">{t('business.quotes.vatExempt')}</SelectItem>
                    </SelectContent>
                  </Select>
                </Field>
                <Field label={t('business.quotes.vatRate')} htmlFor="q-rate">
                  <Input
                    id="q-rate"
                    type="number"
                    dir="ltr"
                    min={0}
                    max={30}
                    disabled={q.vatMode === 'exempt'}
                    value={q.vatRate}
                    onChange={(e) => setQuote({ vatRate: Number(e.target.value) || 0 })}
                  />
                </Field>
              </div>

              <div>
                <h3 className="mb-2 text-sm font-medium text-fg">{t('business.quotes.schedule')}</h3>
                <PaymentTermsEditor
                  value={q.paymentTerms}
                  onChange={(paymentTerms) => setQuote({ paymentTerms })}
                />
              </div>

              <Field
                label={t('business.quotes.terms')}
                htmlFor="q-terms"
                hint={t('business.quotes.termsHint')}
              >
                <Textarea
                  id="q-terms"
                  dir={dirOf()}
                  rows={12}
                  maxLength={4000}
                  value={q.termsText}
                  placeholder={t('business.quotes.termsEmptyPlaceholder')}
                  onChange={(e) => setQuote({ termsText: e.target.value })}
                />
              </Field>
              {!q.termsText.trim() && draft.suggestedTerms ? (
                <div className="flex flex-wrap items-center justify-between gap-3 rounded-(--radius-md) border border-info-border bg-info-subtle px-3 py-2.5 text-xs text-fg">
                  <span>{t('business.quotes.termsEmpty')}</span>
                  <Button type="button" size="sm" variant="secondary" onClick={() => setQuote({ termsText: draft.suggestedTerms })}>
                    {t('business.quotes.useSuggested')}
                  </Button>
                </div>
              ) : null}
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>{t('business.afterApproval.title')}</CardTitle>
              <CardDescription>{t('business.afterApproval.description')}</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <label className="flex items-start justify-between gap-4">
                <span>
                  <span className="block text-sm font-medium text-fg">{t('business.afterApproval.plan')}</span>
                  <span className="block text-xs text-fg-muted">{t('business.afterApproval.planHint')}</span>
                </span>
                <Switch
                  checked={q.afterApproval.plan}
                  onCheckedChange={(plan) =>
                    setQuote({ afterApproval: { ...q.afterApproval, plan } })
                  }
                  aria-label={t('business.afterApproval.plan')}
                />
              </label>
              <label className="flex items-start justify-between gap-4">
                <span>
                  <span className="block text-sm font-medium text-fg">{t('business.afterApproval.autoSchedule')}</span>
                  <span className="block text-xs text-fg-muted">{t('business.afterApproval.autoScheduleHint')}</span>
                </span>
                <Switch
                  checked={q.afterApproval.autoSchedule}
                  disabled={!q.afterApproval.plan}
                  onCheckedChange={(autoSchedule) =>
                    setQuote({ afterApproval: { ...q.afterApproval, autoSchedule } })
                  }
                  aria-label={t('business.afterApproval.autoSchedule')}
                />
              </label>
              <div className="grid gap-4 sm:grid-cols-2">
                <Field
                  label={t('business.afterApproval.materials')}
                  htmlFor="aa-materials"
                  hint={t('business.afterApproval.materialsHint')}
                >
                  <Input
                    id="aa-materials"
                    type="number"
                    inputMode="numeric"
                    dir="ltr"
                    min={0}
                    max={60}
                    value={q.afterApproval.materialsLeadDays}
                    onChange={(e) =>
                      setQuote({
                        afterApproval: {
                          ...q.afterApproval,
                          materialsLeadDays: Math.min(
                            60,
                            Math.max(0, Math.round(Number(e.target.value) || 0)),
                          ),
                        },
                      })
                    }
                  />
                </Field>
                <Field
                  label={t('business.afterApproval.workday')}
                  htmlFor="aa-workday"
                  hint={t('business.afterApproval.workdayHint')}
                >
                  <Input
                    id="aa-workday"
                    type="number"
                    inputMode="decimal"
                    dir="ltr"
                    min={2}
                    max={14}
                    step={0.5}
                    value={q.afterApproval.workdayHours}
                    onChange={(e) =>
                      setQuote({
                        afterApproval: {
                          ...q.afterApproval,
                          workdayHours: Math.min(14, Math.max(2, Number(e.target.value) || 8)),
                        },
                      })
                    }
                  />
                </Field>
              </div>
            </CardContent>
          </Card>
        </>
      ) : null}

      <div className="flex flex-wrap items-center justify-end gap-3">
        {termsIssue ? (
          <p id="business-save-blocked" className="text-xs font-medium text-danger" role="status">
            {scheduleIssueText(termsIssue)}
          </p>
        ) : null}
        <Button
          loading={save.isPending}
          disabled={!valid}
          aria-describedby={termsIssue ? 'business-save-blocked' : undefined}
          onClick={() => save.mutate(draft)}
        >
          {t('common:actions.save')}
        </Button>
      </div>
    </div>
  );
}
